import React, { useState, createContext, useContext, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItemButton,
  Select,
  MenuItem,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  TableChart as TableIcon,
  Assessment as ReportIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  History as HistoryIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { usersApi } from '../services/api';

export type UserRole = 'master_admin' | 'admin' | 'tehnolog' | 'kontrolor' | 'direktor';
export const UserRoleContext = createContext<{
  role: UserRole;
  setRole: (role: UserRole) => void;
}>({ role: 'tehnolog', setRole: () => {} });

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerExpanded, setDrawerExpanded] = useState(!isMobile);
  const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
  const [role, setRole] = useState<UserRole>(userFromStorage.role || 'tehnolog');
  const [users, setUsers] = useState<{ _id: string; username: string; role: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');

  useEffect(() => {
    // console.log('useEffect - role:', role);
    if ((role === 'admin' || role === 'master_admin')) {
      // console.log('useEffect - učitavam sve korisnike...');
      usersApi.getAll()
        .then(res => {
          // console.log('useEffect - učitani korisnici:', res);
          // console.log('useEffect - trenutni korisnik:', userFromStorage);
          // console.log('useEffect - filtrirani korisnici za master_admin:', res.filter((u: any) => (u._id || u.username) !== (userFromStorage._id || userFromStorage.id)));
          // console.log('useEffect - filtrirani korisnici za admin:', res.filter((u: any) => u.role === 'tehnolog'));
          setUsers(res);
        })
        .catch((error) => {
          // console.error('useEffect - greška pri učitavanju korisnika:', error);
          setUsers([]);
        });
    } else {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // console.log('useEffect - nije admin, postavljam samo trenutnog korisnika:', user);
      setUsers(user && user.username ? [user] : []);
    }
  }, [role]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // console.log('useEffect - inicijalno resetujem selectedUser na prazno');
    // Select služi za izbor korisnika ZA impersonaciju, ne za prikaz trenutnog korisnika.
    // Zato inicijalna vrednost treba da bude '', da ne bi bila van opsega dostupnih opcija.
    setSelectedUser('');
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // console.log('useEffect - postavljam role:', user.role);
    setRole(user.role || 'tehnolog');
  }, [selectedUser]);

  // Prati promene na mobilnim uređajima
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
      setDrawerExpanded(false);
    } else {
      setDrawerExpanded(true);
    }
  }, [isMobile]);

  // Kada se otvori sidebar na mobilnim uređajima, automatski ga proširi
  useEffect(() => {
    if (isMobile && drawerOpen) {
      setDrawerExpanded(true);
    }
  }, [drawerOpen, isMobile]);

  const handleUserChange = async (e: any) => {
    const userId = e.target.value;
    // console.log('handleUserChange - userId:', userId);
    setSelectedUser(userId);
    const user = users.find(u => (u._id || u.username) === userId);
    // console.log('handleUserChange - pronađen user:', user);
    if (!user) {
      // console.error('Korisnik nije pronađen u listi:', userId);
      return;
    }
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    // console.log('handleUserChange - currentUser:', currentUser);
    // console.log('handleUserChange - target user role:', user.role);
    // console.log('handleUserChange - currentUser._id:', currentUser._id);
    // console.log('handleUserChange - currentUser.id:', currentUser.id);
    if ((currentUser._id || currentUser.id || currentUser.username) === userId) {
      // console.log('Pokušaj impersonacije samog sebe - ignorisan');
      return;
    }
    try {
      // console.log('handleUserChange - pozivam API...');
      // console.log('handleUserChange - user._id:', user._id);
      // console.log('handleUserChange - user.id:', (user as any).id);
      // console.log('handleUserChange - koristim user._id za API poziv');
      // Proveri da li koristimo ispravan ID
      const userId = user._id || (user as any).id;
      // console.log('handleUserChange - konačni userId za API:', userId);
      const res = await usersApi.impersonate(userId);
      // console.log('handleUserChange - API response:', res);
      if (!localStorage.getItem('impersonation_token')) {
        localStorage.setItem('impersonation_token', localStorage.getItem('token')!);
        localStorage.setItem('impersonation_user', localStorage.getItem('user')!);
      }
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      // console.log('handleUserChange - reloading page...');
      window.location.reload();
    } catch (error: any) {
      // console.error('Greška pri impersonaciji:', error);
      // console.error('Error response:', error.response);
      // console.error('Error message:', error.message);
      // Prikaži grešku korisniku
      const errorMessage = error.response?.data?.error || error.message || 'Nepoznata greška';
      // console.error('Error message to show:', errorMessage);
      alert(`Greška pri impersonaciji: ${errorMessage}`);
      // Vrati select u "neselektovano" stanje da ne drži nepostojeću vrednost
      setSelectedUser('');
    }
  };

  const handleLogout = async () => {
    try {
      // Pozovi logout API
      await usersApi.logout();
    } catch (error) {
      // console.error('Greška pri logout-u:', error);
      // Nastavi sa logout-om čak i ako API ne radi
    } finally {
      // Uvek očisti localStorage i preusmeri
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('impersonation_token');
      localStorage.removeItem('impersonation_user');
      
      // Postavi flag za prikazivanje loading screen-a
      sessionStorage.setItem('shouldShowLoading', 'true');
      
      window.location.href = '/login';
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    ...(role !== 'direktor' ? [{ text: 'Novi unos', icon: <AddIcon />, path: '/new' }] : []),
    ...(role === 'admin' || role === 'master_admin' || role === 'direktor' ? [
      { text: 'Završna tabela', icon: <TableIcon />, path: '/report' },
      { text: 'Izveštaji', icon: <ReportIcon />, path: '/reports' },
    ] : []),
    ...(role === 'admin' || role === 'master_admin' ? [
      { text: 'Administracija proizvoda', icon: <SettingsIcon />, path: '/products-admin' },
      { text: 'Grupe proizvoda', icon: <TableIcon />, path: '/product-groups-admin' },
      { text: 'Korisnici', icon: <PersonIcon />, path: '/users-admin' }
    ] : []),
    ...(role === 'master_admin' ? [
      { text: 'Activity Logs', icon: <HistoryIcon />, path: '/activity-logs' }
    ] : [])
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
      setDrawerExpanded(false);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const originalUser = JSON.parse(localStorage.getItem('impersonation_user') || '{}');
  const isImpersonating = localStorage.getItem('impersonation_token') && localStorage.getItem('impersonation_user') &&
    ((userFromStorage._id && originalUser._id && userFromStorage._id !== originalUser._id) ||
     (userFromStorage.username && originalUser.username && userFromStorage.username !== originalUser.username));

  return (
    <UserRoleContext.Provider value={{ role, setRole }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="fixed" elevation={0} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar sx={{ 
            minHeight: isMobile ? '56px' : '64px',
            background: 'linear-gradient(90deg, #2c3e50 0%, #34495e 100%)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={() => {
                  setDrawerOpen(true);
                  setDrawerExpanded(true);
                }}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                component="div"
                sx={{ 
                  fontSize: isMobile ? '0.9rem' : '1.25rem',
                  lineHeight: 1.2,
                  color: 'white',
                  fontWeight: 600,
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                }}
              >
                {isMobile ? 'Tehnička merenja' : 'Tehnička merenja proizvoda'}
              </Typography>
              {!isMobile && (
                <Typography variant="body2" sx={{ 
                  opacity: 0.9, 
                  fontSize: '0.75rem',
                  color: 'rgba(255,255,255,0.8)',
                  fontWeight: 400
                }}>
                  {role === 'master_admin' ? 'Master Admin' : 
                   role === 'admin' ? 'Administrator' : 
                   role === 'direktor' ? 'Direktor' : 
                   role === 'tehnolog' ? 'Tehnolog' : 
                   role === 'kontrolor' ? 'Kontrolor' : 'Korisnik'}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(role === 'admin' || role === 'master_admin') && users.length > 1 ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ 
                    background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                    color: 'white', 
                    width: isMobile ? 28 : 32, 
                    height: isMobile ? 28 : 32, 
                    mr: isMobile ? 0.5 : 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }
                  }}>
                    <AccountCircleIcon fontSize={isMobile ? "small" : "medium"} />
                  </Avatar>
                  <Select
                    value={selectedUser}
                    onChange={handleUserChange}
                    size="small"
                    sx={{ 
                      color: 'white', 
                      borderColor: 'rgba(255,255,255,0.3)', 
                      '& .MuiSelect-icon': { color: 'white' }, 
                      minWidth: isMobile ? 80 : 120, 
                      background: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      borderRadius: '8px',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.15)',
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      }
                    }}
                    variant="outlined"
                  >
                    {role === 'master_admin' && users.filter(u => (u._id || u.username) !== (userFromStorage._id || userFromStorage.id)).map(u => (
                      <MenuItem key={u._id || u.username} value={u._id || u.username}>
                        {u.username} ({u.role})
                      </MenuItem>
                    ))}
                    {role === 'admin' && users.filter(u => u.role === 'tehnolog').map(u => (
                      <MenuItem key={u._id || u.username} value={u._id || u.username}>
                        {u.username} ({u.role})
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              ) : (
                <Avatar sx={{ 
                  background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                  color: 'white', 
                  width: isMobile ? 28 : 32, 
                  height: isMobile ? 28 : 32,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }
                }}>
                  <AccountCircleIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
              )}
              {isImpersonating && !isMobile && (
                <Button 
                  size="small"
                  onClick={() => {
                    localStorage.setItem('token', localStorage.getItem('impersonation_token')!);
                    localStorage.setItem('user', localStorage.getItem('impersonation_user')!);
                    localStorage.removeItem('impersonation_token');
                    localStorage.removeItem('impersonation_user');
                    window.location.reload();
                  }}
                  sx={{
                    color: 'white',
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      background: 'rgba(255,255,255,0.2)',
                      borderColor: 'rgba(255,255,255,0.5)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Vrati se na originalnog korisnika
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>
        <Drawer
          anchor="left"
          open={isMobile ? drawerOpen : true}
          onClose={() => {
            setDrawerOpen(false);
            if (isMobile) {
              setDrawerExpanded(false);
            }
          }}
          variant={isMobile ? "temporary" : "persistent"}
          sx={{
            '& .MuiBackdrop-root': {
              backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
              backdropFilter: isMobile ? 'blur(4px)' : 'none',
            },
            '& .MuiDrawer-paper': {
              width: isMobile ? '280px' : (drawerExpanded ? 250 : 64),
              boxSizing: 'border-box',
              transition: 'width 0.3s ease',
              overflow: 'hidden',
              background: 'linear-gradient(180deg, #2c3e50 0%, #34495e 100%)',
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              top: isMobile ? '56px' : '64px',
              height: isMobile ? 'calc(100% - 56px)' : 'calc(100% - 64px)',
              borderRadius: 0, // Uklanja zaobljenost
              boxShadow: isMobile ? '4px 0 20px rgba(0, 0, 0, 0.3)' : 'none',
              '@media (max-width: 480px)': {
                width: '85%', // Na vrlo malim ekranima koristi 85% širine
                top: '52px',
                height: 'calc(100% - 52px)',
              },
            },
          }}
        >
          <Box sx={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%',
            maxWidth: isMobile ? '100%' : 250
          }} role="presentation">
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              minHeight: 64,
              background: 'rgba(255, 255, 255, 0.05)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              {drawerExpanded || isMobile ? (
                <>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontSize: isMobile ? '1.1rem' : '1.25rem',
                      color: 'white',
                      fontWeight: 600,
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Tehnička merenja
                  </Typography>
                  <IconButton
                    onClick={() => {
                      if (isMobile) {
                        setDrawerOpen(false);
                        setDrawerExpanded(false);
                      } else {
                        setDrawerExpanded(false);
                      }
                    }}
                    sx={{ 
                      color: 'rgba(255,255,255,0.8)',
                      minWidth: 40,
                      height: 40,
                      '&:hover': {
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    <CloseIcon fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </>
                              ) : (
                  <IconButton
                    onClick={() => setDrawerExpanded(true)}
                    sx={{ 
                      color: 'rgba(255,255,255,0.8)',
                      width: '100%',
                      '&:hover': {
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
            </Box>
            <Divider />
            <List sx={{ flexGrow: 1 }}>
              {menuItems.map((item) => (
                <ListItem
                  key={item.text}
                  disablePadding
                >
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{
                      minHeight: 48,
                      margin: '4px 8px',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.2)',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                      ...(isMobile && {
                        minHeight: 56,
                        margin: '2px 4px',
                        borderRadius: '8px',
                      })
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: isActive(item.path) ? 'white' : 'rgba(255,255,255,0.7)',
                      minWidth: drawerExpanded ? 40 : 'auto',
                      ...(isMobile && {
                        fontSize: '1.2rem'
                      })
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {drawerExpanded && (
                      <ListItemText
                        primary={item.text}
                        sx={{ 
                          color: isActive(item.path) ? 'white' : 'rgba(255,255,255,0.8)',
                          fontWeight: isActive(item.path) ? 600 : 400,
                          ...(isMobile && {
                            fontSize: '0.9rem',
                            '& .MuiListItemText-primary': {
                              fontSize: '0.9rem',
                            }
                          })
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Box sx={{ flexGrow: 1 }} />
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleNavigation('/settings')}
                  sx={{ 
                    minHeight: 48,
                    margin: '4px 8px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                    ...(isMobile && {
                      minHeight: 56,
                      margin: '2px 4px',
                      borderRadius: '8px',
                    })
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: drawerExpanded ? 40 : 'auto',
                    color: 'rgba(255,255,255,0.7)',
                    ...(isMobile && {
                      fontSize: '1.2rem'
                    })
                  }}>
                    <SettingsIcon />
                  </ListItemIcon>
                  {drawerExpanded && (
                    <ListItemText 
                      primary="Podešavanja"
                      sx={{ 
                        color: 'rgba(255,255,255,0.8)',
                        ...(isMobile && {
                          fontSize: '0.9rem',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.9rem',
                          }
                        })
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={handleLogout}
                  sx={{ 
                    minHeight: 48,
                    margin: '4px 8px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                    ...(isMobile && {
                      minHeight: 56,
                      margin: '2px 4px',
                      borderRadius: '8px',
                    })
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: drawerExpanded ? 40 : 'auto',
                    color: 'rgba(255,255,255,0.7)',
                    ...(isMobile && {
                      fontSize: '1.2rem'
                    })
                  }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  {drawerExpanded && (
                    <ListItemText 
                      primary="Logout"
                      sx={{ 
                        color: 'rgba(255,255,255,0.8)',
                        ...(isMobile && {
                          fontSize: '0.9rem',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.9rem',
                          }
                        })
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Drawer>
        <Box component="main" sx={{ 
          flexGrow: 1, 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          marginLeft: isMobile ? 0 : (drawerExpanded ? '250px' : '64px'),
          marginTop: isMobile ? '56px' : '64px',
          transition: 'margin-left 0.3s ease, margin-top 0.3s ease',
          minHeight: 'calc(100vh - 64px)',
          padding: '20px',
          borderRadius: 0 // Uklanja zaobljenost
        }}>
          {children}
        </Box>
        <Box component="footer" sx={{ 
          py: 3, 
          background: 'linear-gradient(90deg, #2c3e50 0%, #34495e 100%)',
          mt: 'auto',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Container maxWidth="lg">
            <Typography variant="body2" sx={{ 
              color: 'rgba(255,255,255,0.8)', 
              align: 'center',
              fontWeight: 400
            }}>
              © 2025 Thermowool - Tehnička merenja proizvoda. Sva prava zadržana.
            </Typography>
          </Container>
        </Box>
      </Box>
    </UserRoleContext.Provider>
  );
};

export default Layout; 